import { useQuery } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, EmptyState, DataTable, StatusPill } from "@/components/common";
import { useApp } from "@/lib/store";
import { fmtDate, shortId } from "@/lib/utils";

interface ComposedPackSummary { id: string; name: string; semver: string }

interface AdminCompositionRun {
  id: string;
  spec: string;
  spec_name: string;
  workspace: string;
  workspace_name: string;
  status: string;
  error_stage: string | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  composed_pack: ComposedPackSummary | null;
  created_at: string;
}

/** Phase 7.4 — cross-workspace composition-run audit trail for staff.
 *  Calls the same pre-existing `/composition/runs/` endpoint used
 *  elsewhere in the app, but with `scopeToWorkspace=false` so it isn't
 *  narrowed to the currently selected workspace — the whole point of an
 *  audit trail is to see every workspace's runs. */
export function AdminComposition() {
  const user = useApp((s) => s.user);
  const workspaceId = useApp((s) => s.workspaceId);
  const workspaces = useApp((s) => s.workspaces);
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser);
  const workspace = workspaces.find((item) => item.id === workspaceId);
  const canCompose = isAdmin || Boolean(workspace?.entitlements?.includes("semantic_composer"));

  const runs = useQuery<{ results: AdminCompositionRun[] }>({
    queryKey: ["composition-runs", workspaceId, isAdmin],
    queryFn: () => rawFetch(
      "/composition/runs/?ordering=-created_at&page_size=100",
      undefined,
      "json",
      !isAdmin,
    ),
    enabled: canCompose && (isAdmin || Boolean(workspaceId)),
  });

  if (!canCompose) {
    return (
      <>
        <PageHeader title="Composition Runs" subtitle="Composition run history and validation outcomes." />
        <EmptyState title="Composer entitlement required" hint="Select an Enterprise workspace with the semantic_composer entitlement." />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Composition Runs"
                  subtitle={isAdmin
                    ? "Staff audit across accessible workspaces."
                    : `Run history for ${workspace?.name ?? "the selected workspace"}, including validation and failure details.`} />

      {runs.isError && <ErrorBox error={runs.error} />}
      <DataTable
        rows={runs.data?.results ?? []}
        empty="No composition runs recorded yet."
        cols={[
          { header: "ID",        cell: (r) => <code className="font-mono text-xs">{shortId(r.id)}</code> },
          { header: "Spec",      cell: (r) => r.spec_name || <code className="font-mono text-xs">{shortId(r.spec)}</code> },
          { header: "Workspace", cell: (r) => r.workspace_name || <code className="font-mono text-xs">{shortId(r.workspace)}</code> },
          { header: "Status",    cell: (r) => <StatusPill status={r.status} /> },
          { header: "Composed pack", cell: (r) => r.composed_pack
              ? <code className="font-mono text-xs">{r.composed_pack.name}@{r.composed_pack.semver}</code>
              : "—" },
          { header: "Started",   cell: (r) => fmtDate(r.started_at) },
          { header: "Completed", cell: (r) => fmtDate(r.completed_at) },
          { header: "Error",     cell: (r) => r.error_message
              ? <span className="text-xs text-bad">{r.error_stage}: {r.error_message}</span>
              : "—" },
        ]}
      />
    </>
  );
}
